/**
 * DecisionFlow — SpiffPlanManager
 * data-fit LLC · Confidential
 * Domain: §47 SPIFF / Tactical Incentives (m075)
 * Spec authority: decisionflow_spiff_incentives_spec_v1_0.md §C + §D
 * API Contract: api_contract_v1_31.md §47
 * Wizard substages:
 *   spiff_plan_definition · spiff_rule_configuration ·
 *   spiff_position_assignment · spiff_validation
 *
 * Two chains based on trigger_type:
 *   transaction_triggered → plan → rules (required, ≥1) → assign → submit → approve
 *   manager_assigned      → plan → assign → submit → approve → award → award submit → award approve
 *
 * Feature gate: feature.tier.spiff_incentives — enterprise only.
 *   403 FEATURE_UNAVAILABLE → GateStatusBanner.
 * tenant_super_admin excluded (NN-36).
 * State derived from approved_by / submitted_at — no plan_status column.
 * onReject={null} — reject endpoint not confirmed for §47 plans.
 * Award approval uses variant="spiff_award" — separate ApprovalPanel instance.
 * Path: ui/src/components/tenant-admin/SpiffPlanManager.jsx
 *
 * S312 — OI-S307-UI-THEME: Full CSS token compliance pass.
 *   StatusPill prop corrected: state= → status= (shared StatusPill expects `status` prop).
 *   inputClass updated: added focus:ring-2 focus:ring-[rgb(var(--color-focus-ring))] token.
 *   All other token usage already compliant from prior pass.
 *   No logic, workflow, or structural changes.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';
import { useFeatures } from '../../auth/FeatureContext.jsx';
import { apiGet, apiPost } from '../../api/client';
import ApprovalPanel from '../shared/ApprovalPanel.jsx';
import GateStatusBanner from '../shared/GateStatusBanner.jsx';
import StatusPill from '../shared/StatusPill.jsx';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TRIGGER_TYPES = [
  { value: 'transaction_triggered', label: 'Transaction-triggered' },
  { value: 'manager_assigned',      label: 'Manager-assigned' },
];

const PAYOUT_TYPES = [
  { value: 'flat_amount',   label: 'Flat amount' },
  { value: 'percentage',    label: 'Percentage of commissionable amount' },
  { value: 'tiered_flat',   label: 'Tiered flat' },
  { value: 'tiered_pct',    label: 'Tiered percentage' },
];

const TABS = [
  { key: 'plans',       label: 'SPIFF Plans'         },
  { key: 'assignments', label: 'Position Assignments' },
  { key: 'awards',      label: 'Manager Awards'       },
];

const EMPTY_PLAN_FORM = {
  plan_code:            '',
  plan_name:            '',
  trigger_type:         'transaction_triggered',
  payout_type:          'flat_amount',
  payout_cap_amount:    '',
  effective_start_date: '',
  effective_end_date:   '',
};

const EMPTY_RULE_FORM = {
  rule_priority:              '1',
  product_line:               '',
  min_commissionable_amount:  '',
  flat_amount:                '',
  payout_pct:                 '',
  // tier_schedule_json built separately
};

const EMPTY_ASSIGNMENT_FORM = {
  position_id:      '',
  fiscal_period_id: '',
};

const EMPTY_AWARD_FORM = {
  spiff_plan_id:    '',
  position_id:      '',
  fiscal_period_id: '',
  award_reason:     '',
  award_amount:     '',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function deriveState(entity) {
  if (entity.approved_by)                         return 'approved';
  if (entity.submitted_at || entity.submitted_by) return 'pending_approval';
  return 'draft';
}

function getApiErrorCode(error) { return error?.code || null; }
function getErrorMessage(error, fallback) { return error?.message || fallback; }

// ---------------------------------------------------------------------------
// Shared class strings
// ---------------------------------------------------------------------------

const thClass      = 'px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]';
const tdClass      = 'px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] border-b border-[rgb(var(--color-border))]';
const btnPrimary   = 'rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-sm font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50 disabled:cursor-not-allowed';
const btnSecondary = 'rounded-lg bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50 disabled:cursor-not-allowed';
const inputClass   = 'mt-1 block w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-2 text-tok-sm focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-focus-ring))]';
const labelClass   = 'flex flex-col text-tok-sm font-medium text-[rgb(var(--color-text))]';

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function SpiffPlanManager({ mode, period, wizardSteps, embedded = false, planId, planComponentId, fiscalPeriodId, readOnly = false }) {
  const { user } = useAuth();
  const { hasPlanType } = useFeatures();

  if (!hasPlanType('feature.plan.spiff')) {
    return <GateStatusBanner code="PLAN_TYPE_NOT_ENTITLED" />;
  }

  // Note: SpiffPlanManager is plan-level, not component-scoped.
  // planComponentId prop accepted for API consistency but unused.

  const [activeTab, setActiveTab]                 = useState('plans');
  const [featureUnavailable, setFeatureUnavailable] = useState(false);
  const [toast, setToast]                         = useState(null);

  // Plans
  const [plans, setPlans]                         = useState([]);
  const [plansLoading, setPlansLoading]           = useState(false);
  const [plansError, setPlansError]               = useState('');
  const [selectedPlan, setSelectedPlan]           = useState(null);
  const [detailLoading, setDetailLoading]         = useState(false);
  const [showPlanForm, setShowPlanForm]           = useState(false);
  const [planForm, setPlanForm]                   = useState(EMPTY_PLAN_FORM);
  const [planSaving, setPlanSaving]               = useState(false);
  const [planFormError, setPlanFormError]         = useState('');
  const [actionLoading, setActionLoading]         = useState(false);
  const [actionError, setActionError]             = useState('');

  // Rules (transaction_triggered only)
  const [showRuleForm, setShowRuleForm]           = useState(false);
  const [ruleForm, setRuleForm]                   = useState(EMPTY_RULE_FORM);
  const [ruleSaving, setRuleSaving]               = useState(false);
  const [ruleError, setRuleError]                 = useState('');
  const [planRules, setPlanRules]                 = useState([]);

  // Assignments
  const [assignments, setAssignments]             = useState([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [assignmentsError, setAssignmentsError]   = useState('');
  const [assigningPlanId, setAssigningPlanId]     = useState('');
  const [assignForm, setAssignForm]               = useState(EMPTY_ASSIGNMENT_FORM);
  const [assignSaving, setAssignSaving]           = useState(false);
  const [assignFormError, setAssignFormError]     = useState('');

  // Awards (manager_assigned only)
  const [awards, setAwards]                       = useState([]);
  const [awardsLoading, setAwardsLoading]         = useState(false);
  const [awardsError, setAwardsError]             = useState('');
  const [showAwardForm, setShowAwardForm]         = useState(false);
  const [awardForm, setAwardForm]                 = useState(EMPTY_AWARD_FORM);
  const [awardSaving, setAwardSaving]             = useState(false);
  const [awardFormError, setAwardFormError]       = useState('');
  const [selectedAward, setSelectedAward]         = useState(null);
  const [awardActionLoading, setAwardActionLoading] = useState(false);
  const [awardActionError, setAwardActionError]   = useState('');

  // Reference data
  const [positions, setPositions]                 = useState([]);

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  const showToast = useCallback((message) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const currentUserId = user?.user_id || user?.id || null;

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------

  const loadPlans = useCallback(async () => {
    if (!user) return;
    setPlansLoading(true);
    setPlansError('');
    try {
      // C7 — S689 plan_id scoping: filter spiff plans to this plan when planId injected.
      const data = await apiGet(`/api/v1/spiff-plans${planId != null ? '?plan_id=' + planId : ''}`);
      setPlans(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      if (getApiErrorCode(error) === 'FEATURE_UNAVAILABLE') {
        setFeatureUnavailable(true);
        return;
      }
      setPlansError(getErrorMessage(error, 'Unable to load SPIFF plans.'));
    } finally {
      setPlansLoading(false);
    }
  }, [user]);

  const loadPlanDetail = useCallback(async (planId) => {
    setDetailLoading(true);
    setActionError('');
    setPlanRules([]);
    try {
      const data = await apiGet(`/api/v1/spiff-plans/${planId}`);
      setSelectedPlan(data);
      if (data?.rules) setPlanRules(data.rules);
    } catch (error) {
      setActionError(getErrorMessage(error, 'Unable to load SPIFF plan detail.'));
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const loadAssignments = useCallback(async () => {
    if (!user) return;
    setAssignmentsLoading(true);
    setAssignmentsError('');
    try {
      const periodId = period?.fiscal_period_id;
      const url = periodId
        ? `/api/v1/spiff-assignments?fiscal_period_id=${periodId}`
        : '/api/v1/spiff-assignments';
      const data = await apiGet(url);
      setAssignments(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      setAssignmentsError(getErrorMessage(error, 'Unable to load SPIFF assignments.'));
    } finally {
      setAssignmentsLoading(false);
    }
  }, [user, period]);

  const loadAwards = useCallback(async () => {
    if (!user) return;
    setAwardsLoading(true);
    setAwardsError('');
    try {
      const periodId = period?.fiscal_period_id;
      const url = periodId
        ? `/api/v1/spiff-awards?fiscal_period_id=${periodId}`
        : '/api/v1/spiff-awards';
      const data = await apiGet(url);
      setAwards(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      setAwardsError(getErrorMessage(error, 'Unable to load SPIFF awards.'));
    } finally {
      setAwardsLoading(false);
    }
  }, [user, period]);

  const loadPositions = useCallback(async () => {
    if (!user) return;
    try {
      const data = await apiGet('/api/v1/positions');
      setPositions(Array.isArray(data) ? data : data?.positions || data?.data || []);
    } catch { /* non-blocking */ }
  }, [user]);

  useEffect(() => { loadPlans(); loadPositions(); }, [loadPlans, loadPositions]);
  useEffect(() => { if (activeTab === 'assignments') loadAssignments(); }, [activeTab, loadAssignments]);
  useEffect(() => { if (activeTab === 'awards') loadAwards(); }, [activeTab, loadAwards]);

  // ---------------------------------------------------------------------------
  // Derived
  // ---------------------------------------------------------------------------

  const approvedPlans = useMemo(() => plans.filter((p) => deriveState(p) === 'approved'), [plans]);
  const managerAssignedApprovedPlans = useMemo(
    () => approvedPlans.filter((p) => p.trigger_type === 'manager_assigned'),
    [approvedPlans]
  );

  const positionNameById = useMemo(
    () => positions.reduce((acc, p) => { acc[String(p.position_id)] = p.full_name || `Position ${p.position_id}`; return acc; }, {}),
    [positions]
  );

  const planNameById = useMemo(
    () => plans.reduce((acc, p) => { acc[String(p.spiff_plan_id)] = p.plan_name || p.plan_code; return acc; }, {}),
    [plans]
  );

  // ---------------------------------------------------------------------------
  // Plan form
  // ---------------------------------------------------------------------------

  const updatePlanForm = useCallback((field, value) => setPlanForm((prev) => ({ ...prev, [field]: value })), []);
  const resetPlanForm  = useCallback(() => { setPlanForm(EMPTY_PLAN_FORM); setPlanFormError(''); }, []);

  const validatePlanForm = useCallback(() => {
    if (!planForm.plan_code.trim())            return 'Plan code is required.';
    if (!planForm.plan_name.trim())            return 'Plan name is required.';
    if (!planForm.trigger_type)                return 'Trigger type is required.';
    if (!planForm.payout_type)                 return 'Payout type is required.';
    if (!planForm.effective_start_date)        return 'Effective start date is required.';
    if (planForm.effective_end_date && planForm.effective_end_date < planForm.effective_start_date)
      return 'Effective end date must be after start date.';
    return '';
  }, [planForm]);

  const createPlan = useCallback(async () => {
    const err = validatePlanForm();
    if (err) { setPlanFormError(err); return; }
    setPlanSaving(true); setPlanFormError('');
    try {
      const payload = {
        plan_code:            planForm.plan_code.trim(),
        plan_name:            planForm.plan_name.trim(),
        trigger_type:         planForm.trigger_type,
        payout_type:          planForm.payout_type,
        payout_cap_amount:    planForm.payout_cap_amount || null,
        effective_start_date: planForm.effective_start_date,
        effective_end_date:   planForm.effective_end_date || null,
      };
      const created = await apiPost('/api/v1/spiff-plans', payload);
      await loadPlans();
      setShowPlanForm(false);
      resetPlanForm();
      showToast('SPIFF plan created.');
      if (created?.spiff_plan_id) await loadPlanDetail(created.spiff_plan_id);
    } catch (error) {
      setPlanFormError(getErrorMessage(error, 'Unable to create SPIFF plan.'));
    } finally {
      setPlanSaving(false);
    }
  }, [loadPlanDetail, loadPlans, planForm, resetPlanForm, showToast, validatePlanForm]);

  // ---------------------------------------------------------------------------
  // Plan approval actions
  // ---------------------------------------------------------------------------

  const submitPlan = useCallback(async () => {
    if (!selectedPlan) return;
    setActionLoading(true); setActionError('');
    try {
      await apiPost(`/api/v1/spiff-plans/${selectedPlan.spiff_plan_id}/submit`, {});
      await loadPlanDetail(selectedPlan.spiff_plan_id);
      await loadPlans();
      showToast('SPIFF plan submitted for approval.');
    } catch (error) {
      setActionError(getErrorMessage(error, 'Unable to submit SPIFF plan.'));
    } finally {
      setActionLoading(false);
    }
  }, [loadPlanDetail, loadPlans, selectedPlan, showToast]);

  const approvePlan = useCallback(async () => {
    if (!selectedPlan) return;
    setActionLoading(true); setActionError('');
    try {
      await apiPost(`/api/v1/spiff-plans/${selectedPlan.spiff_plan_id}/approve`, {});
      await loadPlanDetail(selectedPlan.spiff_plan_id);
      await loadPlans();
      showToast('SPIFF plan approved.');
    } catch (error) {
      setActionError(getErrorMessage(error, 'Unable to approve SPIFF plan.'));
    } finally {
      setActionLoading(false);
    }
  }, [loadPlanDetail, loadPlans, selectedPlan, showToast]);

  // ---------------------------------------------------------------------------
  // Rule form (transaction_triggered only)
  // ---------------------------------------------------------------------------

  const updateRuleForm = useCallback((field, value) => setRuleForm((prev) => ({ ...prev, [field]: value })), []);

  const validateRuleForm = useCallback(() => {
    if (!ruleForm.rule_priority || Number(ruleForm.rule_priority) < 1) return 'Rule priority must be a positive integer.';
    const hasPayout = ruleForm.flat_amount || ruleForm.payout_pct;
    if (!hasPayout) return 'Exactly one payout method (flat amount or percentage) is required.';
    if (ruleForm.flat_amount && ruleForm.payout_pct) return 'Only one payout method allowed per rule.';
    return '';
  }, [ruleForm]);

  const createRule = useCallback(async () => {
    if (!selectedPlan) return;
    const err = validateRuleForm();
    if (err) { setRuleError(err); return; }
    setRuleSaving(true); setRuleError('');
    try {
      const payload = {
        rule_priority:             Number(ruleForm.rule_priority),
        product_line:              ruleForm.product_line || null,
        min_commissionable_amount: ruleForm.min_commissionable_amount || null,
        flat_amount:               ruleForm.flat_amount || null,
        payout_pct:                ruleForm.payout_pct || null,
        tier_schedule_json:        null,
      };
      await apiPost(`/api/v1/spiff-plans/${selectedPlan.spiff_plan_id}/rules`, payload);
      await loadPlanDetail(selectedPlan.spiff_plan_id);
      setRuleForm(EMPTY_RULE_FORM);
      setShowRuleForm(false);
      showToast('SPIFF rule added.');
    } catch (error) {
      setRuleError(getErrorMessage(error, 'Unable to create SPIFF rule.'));
    } finally {
      setRuleSaving(false);
    }
  }, [loadPlanDetail, ruleForm, selectedPlan, showToast, validateRuleForm]);

  // ---------------------------------------------------------------------------
  // Assignment actions
  // ---------------------------------------------------------------------------

  const updateAssignForm = useCallback((field, value) => setAssignForm((prev) => ({ ...prev, [field]: value })), []);

  const createAssignment = useCallback(async () => {
    if (!assigningPlanId) { setAssignFormError('Select a plan first.'); return; }
    if (!assignForm.position_id) { setAssignFormError('Position is required.'); return; }
    if (!assignForm.fiscal_period_id) { setAssignFormError('Fiscal period is required.'); return; }
    setAssignSaving(true); setAssignFormError('');
    try {
      await apiPost(`/api/v1/spiff-plans/${assigningPlanId}/assignments`, {
        position_id:      assignForm.position_id,
        fiscal_period_id: Number(assignForm.fiscal_period_id),
      });
      await loadAssignments();
      setAssignForm(EMPTY_ASSIGNMENT_FORM);
      showToast('SPIFF plan assigned to position.');
    } catch (error) {
      setAssignFormError(getErrorMessage(error, 'Unable to create SPIFF assignment.'));
    } finally {
      setAssignSaving(false);
    }
  }, [assignForm, assigningPlanId, loadAssignments, showToast]);

  // ---------------------------------------------------------------------------
  // Award actions
  // ---------------------------------------------------------------------------

  const updateAwardForm = useCallback((field, value) => setAwardForm((prev) => ({ ...prev, [field]: value })), []);

  const validateAwardForm = useCallback(() => {
    if (!awardForm.spiff_plan_id)    return 'SPIFF plan is required.';
    if (!awardForm.position_id)      return 'Position is required.';
    if (!awardForm.fiscal_period_id) return 'Fiscal period is required.';
    if (!awardForm.award_reason.trim()) return 'Award reason is required.';
    if (!awardForm.award_amount || Number(awardForm.award_amount) < 0) return 'Award amount must be non-negative.';
    return '';
  }, [awardForm]);

  const createAward = useCallback(async () => {
    const err = validateAwardForm();
    if (err) { setAwardFormError(err); return; }
    setAwardSaving(true); setAwardFormError('');
    try {
      await apiPost('/api/v1/spiff-awards', {
        spiff_plan_id:    Number(awardForm.spiff_plan_id),
        position_id:      awardForm.position_id,
        fiscal_period_id: Number(awardForm.fiscal_period_id),
        award_reason:     awardForm.award_reason.trim(),
        award_amount:     awardForm.award_amount,
      });
      await loadAwards();
      setAwardForm(EMPTY_AWARD_FORM);
      setShowAwardForm(false);
      showToast('SPIFF award created.');
    } catch (error) {
      setAwardFormError(getErrorMessage(error, 'Unable to create SPIFF award.'));
    } finally {
      setAwardSaving(false);
    }
  }, [awardForm, loadAwards, showToast, validateAwardForm]);

  const submitAward = useCallback(async () => {
    if (!selectedAward) return;
    setAwardActionLoading(true); setAwardActionError('');
    try {
      await apiPost(`/api/v1/spiff-awards/${selectedAward.spiff_award_id}/submit`, {});
      const refreshed = await apiGet(`/api/v1/spiff-awards?fiscal_period_id=${selectedAward.fiscal_period_id}`);
      const updated = (Array.isArray(refreshed) ? refreshed : refreshed?.data || [])
        .find((a) => a.spiff_award_id === selectedAward.spiff_award_id);
      if (updated) setSelectedAward(updated);
      await loadAwards();
      showToast('SPIFF award submitted for approval.');
    } catch (error) {
      setAwardActionError(getErrorMessage(error, 'Unable to submit SPIFF award.'));
    } finally {
      setAwardActionLoading(false);
    }
  }, [loadAwards, selectedAward, showToast]);

  const approveAward = useCallback(async () => {
    if (!selectedAward) return;
    setAwardActionLoading(true); setAwardActionError('');
    try {
      await apiPost(`/api/v1/spiff-awards/${selectedAward.spiff_award_id}/approve`, {});
      const refreshed = await apiGet(`/api/v1/spiff-awards?fiscal_period_id=${selectedAward.fiscal_period_id}`);
      const updated = (Array.isArray(refreshed) ? refreshed : refreshed?.data || [])
        .find((a) => a.spiff_award_id === selectedAward.spiff_award_id);
      if (updated) setSelectedAward(updated);
      await loadAwards();
      showToast('SPIFF award approved.');
    } catch (error) {
      setAwardActionError(getErrorMessage(error, 'Unable to approve SPIFF award.'));
    } finally {
      setAwardActionLoading(false);
    }
  }, [loadAwards, selectedAward, showToast]);

  // ---------------------------------------------------------------------------
  // Feature gate
  // ---------------------------------------------------------------------------

  if (featureUnavailable) return <GateStatusBanner feature="SPIFF Incentives" />;

  if (!user) {
    return (
      <section className="flex flex-col gap-6">
        <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">SPIFF Plans</h2>
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">Authentication required.</div>
      </section>
    );
  }

  // ---------------------------------------------------------------------------
  // Plan detail view (plan + rules + approval)
  // ---------------------------------------------------------------------------

  if (selectedPlan) {
    const state = deriveState(selectedPlan);
    const isTransactionTriggered = selectedPlan.trigger_type === 'transaction_triggered';
    const needsRules = isTransactionTriggered && planRules.length === 0 && state === 'draft';

    return (
      <section className="flex flex-col gap-6">
        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <button type="button" className={btnSecondary}
              onClick={() => { setSelectedPlan(null); setActionError(''); setShowRuleForm(false); }}>
              ← Back to list
            </button>
            <h2 className="mt-3 text-tok-xl font-semibold text-[rgb(var(--color-text))]">{selectedPlan.plan_name}</h2>
            <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
              {selectedPlan.plan_code} ·{' '}
              {TRIGGER_TYPES.find((t) => t.value === selectedPlan.trigger_type)?.label || selectedPlan.trigger_type} ·{' '}
              {PAYOUT_TYPES.find((t) => t.value === selectedPlan.payout_type)?.label || selectedPlan.payout_type}
            </p>
          </div>
          <StatusPill status={state} />
        </header>

        {actionError && (
          <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">{actionError}</div>
        )}

        {/* Plan detail */}
        <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-3 shadow-sm">
          <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Plan details</h3>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-tok-sm">
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Trigger type</dt>
              <dd className="text-[rgb(var(--color-text))]">{TRIGGER_TYPES.find((t) => t.value === selectedPlan.trigger_type)?.label}</dd>
            </div>
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Payout type</dt>
              <dd className="text-[rgb(var(--color-text))]">{PAYOUT_TYPES.find((t) => t.value === selectedPlan.payout_type)?.label}</dd>
            </div>
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Payout cap</dt>
              <dd className="text-[rgb(var(--color-text))]">
                {selectedPlan.payout_cap_amount
                  ? Number(selectedPlan.payout_cap_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })
                  : 'No cap'}
              </dd>
            </div>
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Effective dates</dt>
              <dd className="text-[rgb(var(--color-text))]">
                {selectedPlan.effective_start_date} → {selectedPlan.effective_end_date || 'Open-ended'}
              </dd>
            </div>
          </dl>
        </div>

        {/* Rules section — transaction_triggered only */}
        {isTransactionTriggered && (
          <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
            <div className="flex items-center justify-between gap-2">
              <div>
                <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Qualification rules</h3>
                <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                  At least one rule required before submission. Each rule must have exactly one payout method.
                </p>
              </div>
              {state === 'draft' && (
                <button type="button" className={btnSecondary}
                  onClick={() => { setShowRuleForm(true); setRuleError(''); }}>
                  + Add rule
                </button>
              )}
            </div>

            {needsRules && (
              <div className="rounded-lg bg-[rgb(var(--color-warning))]/10 border border-[rgb(var(--color-warning))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-warning))]">
                No rules defined. At least one rule is required before this plan can be submitted.
              </div>
            )}

            {/* Rule create form */}
            {showRuleForm && (
              <div className="rounded-lg border border-[rgb(var(--color-border))] p-4 flex flex-col gap-3">
                <h4 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">New rule</h4>
                {ruleError && (
                  <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{ruleError}</div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <label className={labelClass}>
                    Priority
                    <input type="number" min="1" className={inputClass} value={ruleForm.rule_priority}
                      onChange={(e) => updateRuleForm('rule_priority', e.target.value)} />
                  </label>
                  <label className={labelClass}>
                    Product line
                    <input type="text" className={inputClass} value={ruleForm.product_line}
                      onChange={(e) => updateRuleForm('product_line', e.target.value)}
                      placeholder="Optional filter" />
                  </label>
                  <label className={labelClass}>
                    Min commissionable amount
                    <input type="number" min="0" step="0.01" className={inputClass}
                      value={ruleForm.min_commissionable_amount}
                      onChange={(e) => updateRuleForm('min_commissionable_amount', e.target.value)}
                      placeholder="Optional threshold" />
                  </label>
                </div>
                <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
                  Payout method — choose exactly one:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className={labelClass}>
                    Flat amount
                    <input type="number" min="0" step="0.01" className={inputClass}
                      value={ruleForm.flat_amount}
                      onChange={(e) => updateRuleForm('flat_amount', e.target.value)}
                      placeholder="Leave blank if using payout %" />
                  </label>
                  <label className={labelClass}>
                    Payout %
                    <input type="number" min="0" max="1" step="0.0001" className={inputClass}
                      value={ruleForm.payout_pct}
                      onChange={(e) => updateRuleForm('payout_pct', e.target.value)}
                      placeholder="Leave blank if using flat amount" />
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" className={btnPrimary} onClick={createRule} disabled={ruleSaving}>
                    {ruleSaving ? 'Saving…' : 'Add rule'}
                  </button>
                  <button type="button" className={btnSecondary}
                    onClick={() => { setShowRuleForm(false); setRuleForm(EMPTY_RULE_FORM); setRuleError(''); }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Rules table */}
            {planRules.length > 0 && (
              <div className="overflow-x-auto">
                <table className="min-w-full text-tok-sm border-collapse">
                  <thead>
                    <tr>
                      <th className={thClass}>Priority</th>
                      <th className={thClass}>Product line</th>
                      <th className={thClass}>Min amount</th>
                      <th className={thClass}>Flat amount</th>
                      <th className={thClass}>Payout %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {planRules.map((rule, i) => (
                      <tr key={rule.spiff_rule_id || i} className="hover:bg-[rgb(var(--color-surface-muted))]">
                        <td className={tdClass}>{rule.rule_priority}</td>
                        <td className={tdClass}>{rule.product_line || '—'}</td>
                        <td className={tdClass}>{rule.min_commissionable_amount || '—'}</td>
                        <td className={tdClass}>{rule.flat_amount || '—'}</td>
                        <td className={tdClass}>{rule.payout_pct || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Approval panel */}
        {detailLoading ? (
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading…</p>
        ) : (
          <ApprovalPanel
            variant="spiff_plan"
            currentState={state}
            submittedBy={selectedPlan.submitted_by}
            currentUserId={currentUserId}
            onSubmit={state === 'draft' ? submitPlan : null}
            onApprove={state === 'pending_approval' ? approvePlan : null}
            onReject={null}
            approvedBy={selectedPlan.approved_by}
            approvedAt={selectedPlan.approved_at}
            loading={actionLoading}
          />
        )}
      </section>
    );
  }

  // ---------------------------------------------------------------------------
  // Award detail view
  // ---------------------------------------------------------------------------

  if (selectedAward) {
    const awardState = deriveState(selectedAward);
    return (
      <section className="flex flex-col gap-6">
        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <button type="button" className={btnSecondary}
              onClick={() => { setSelectedAward(null); setAwardActionError(''); }}>
              ← Back to awards
            </button>
            <h2 className="mt-3 text-tok-xl font-semibold text-[rgb(var(--color-text))]">SPIFF Award</h2>
            <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
              {positionNameById[String(selectedAward.position_id)] || selectedAward.position_id} ·
              Period {selectedAward.fiscal_period_id}
            </p>
          </div>
          <StatusPill status={awardState} />
        </header>

        {awardActionError && (
          <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">{awardActionError}</div>
        )}

        <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-3 shadow-sm">
          <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Award details</h3>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-tok-sm">
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">SPIFF plan</dt>
              <dd className="text-[rgb(var(--color-text))]">{planNameById[String(selectedAward.spiff_plan_id)] || selectedAward.spiff_plan_id}</dd>
            </div>
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Award amount</dt>
              <dd className="text-[rgb(var(--color-text))]">
                {selectedAward.award_amount != null
                  ? Number(selectedAward.award_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })
                  : '—'}
              </dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Award reason</dt>
              <dd className="text-[rgb(var(--color-text))]">{selectedAward.award_reason || '—'}</dd>
            </div>
          </dl>
        </div>

        <ApprovalPanel
          variant="spiff_award"
          currentState={awardState}
          submittedBy={selectedAward.submitted_by}
          currentUserId={currentUserId}
          onSubmit={awardState === 'draft' ? submitAward : null}
          onApprove={awardState === 'pending_approval' ? approveAward : null}
          onReject={null}
          approvedBy={selectedAward.approved_by}
          approvedAt={selectedAward.approved_at}
          loading={awardActionLoading}
        />
      </section>
    );
  }

  // ---------------------------------------------------------------------------
  // Main tabbed view
  // ---------------------------------------------------------------------------

  return (
    <section className="flex flex-col gap-6">
      {toast && (
        <div role="status" className="fixed bottom-4 right-4 z-50 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-3 text-tok-sm text-white shadow-lg">{toast}</div>
      )}

      <header>
        <p className="text-tok-xs font-semibold uppercase tracking-widest text-[rgb(var(--color-text-muted))] mb-1">Stage 8D</p>
        <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">SPIFF / Tactical Incentives</h2>
        <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
          Configure SPIFF plans, assign positions, and manage manager-awarded incentives.
          Transaction-triggered plans require qualification rules before submission.
        </p>
      </header>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[rgb(var(--color-border))]">
        {TABS.map((tab) => (
          <button key={tab.key} type="button" onClick={() => setActiveTab(tab.key)}
            className={[
              'px-4 py-2 text-tok-sm font-medium border-b-2 -mb-px transition-colors',
              activeTab === tab.key
                ? 'border-[rgb(var(--color-primary))] text-[rgb(var(--color-primary))]'
                : 'border-transparent text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]',
            ].join(' ')}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* TAB: SPIFF Plans                                                    */}
      {/* ------------------------------------------------------------------ */}
      {activeTab === 'plans' && (
        <div className="flex flex-col gap-6">
          <div className="flex justify-end">
            <button type="button" className={btnPrimary}
              onClick={() => { setShowPlanForm(true); setPlanFormError(''); }} disabled={showPlanForm}>
              + New SPIFF plan
            </button>
          </div>

          {plansError && (
            <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">{plansError}</div>
          )}

          {showPlanForm && (
            <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
              <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">New SPIFF plan</h3>
              {planFormError && (
                <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">{planFormError}</div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className={labelClass}>Plan code
                  <input type="text" className={inputClass} value={planForm.plan_code}
                    onChange={(e) => updatePlanForm('plan_code', e.target.value)}
                    placeholder="e.g. Q2_PRODUCT_LAUNCH_SPIFF" />
                </label>
                <label className={labelClass}>Plan name
                  <input type="text" className={inputClass} value={planForm.plan_name}
                    onChange={(e) => updatePlanForm('plan_name', e.target.value)}
                    placeholder="e.g. Q2 Product Launch SPIFF" />
                </label>
                <label className={labelClass}>Trigger type
                  <select className={inputClass} value={planForm.trigger_type}
                    onChange={(e) => updatePlanForm('trigger_type', e.target.value)}>
                    {TRIGGER_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </label>
                <label className={labelClass}>Payout type
                  <select className={inputClass} value={planForm.payout_type}
                    onChange={(e) => updatePlanForm('payout_type', e.target.value)}>
                    {PAYOUT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </label>
                <label className={labelClass}>Payout cap amount
                  <input type="number" min="0" step="0.01" className={inputClass}
                    value={planForm.payout_cap_amount}
                    onChange={(e) => updatePlanForm('payout_cap_amount', e.target.value)}
                    placeholder="Leave blank for no cap" />
                </label>
                <div className="flex flex-col gap-2">
                  <label className={labelClass}>Effective start date
                    <input type="date" className={inputClass} value={planForm.effective_start_date}
                      onChange={(e) => updatePlanForm('effective_start_date', e.target.value)} />
                  </label>
                </div>
                <label className={labelClass}>Effective end date
                  <input type="date" className={inputClass} value={planForm.effective_end_date}
                    onChange={(e) => updatePlanForm('effective_end_date', e.target.value)} />
                  <span className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">Leave blank for open-ended.</span>
                </label>
              </div>
              {planForm.trigger_type === 'transaction_triggered' && (
                <div className="rounded-md bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] px-4 py-3 text-tok-sm text-[rgb(var(--color-text-muted))]">
                  Transaction-triggered plans require at least one qualification rule before submission.
                  Rules are added after the plan is created.
                </div>
              )}
              <div className="flex items-center gap-2">
                <button type="button" className={btnPrimary} onClick={createPlan} disabled={planSaving}>
                  {planSaving ? 'Creating…' : 'Create SPIFF plan'}
                </button>
                <button type="button" className={btnSecondary}
                  onClick={() => { setShowPlanForm(false); resetPlanForm(); }} disabled={planSaving}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Plans table */}
          <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">SPIFF plans</h3>
              <button type="button" className={btnSecondary} onClick={loadPlans} disabled={plansLoading}>Refresh</button>
            </div>
            {plansLoading && <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading…</p>}
            {!plansLoading && plans.length === 0 && (
              <p className="text-tok-sm text-[rgb(var(--color-text-muted))] italic">No SPIFF plans created yet.</p>
            )}
            {plans.length > 0 && (
              <div className="overflow-x-auto">
                <table className="min-w-full text-tok-sm border-collapse">
                  <thead>
                    <tr>
                      <th className={thClass}>Plan code</th>
                      <th className={thClass}>Plan name</th>
                      <th className={thClass}>Trigger type</th>
                      <th className={thClass}>Effective dates</th>
                      <th className={thClass}>Status</th>
                      <th className={thClass} aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {plans.map((plan) => {
                      const state = deriveState(plan);
                      return (
                        <tr key={plan.spiff_plan_id} className="hover:bg-[rgb(var(--color-surface-muted))]">
                          <td className={tdClass}>{plan.plan_code}</td>
                          <td className={tdClass}>{plan.plan_name}</td>
                          <td className={tdClass}>{TRIGGER_TYPES.find((t) => t.value === plan.trigger_type)?.label || plan.trigger_type}</td>
                          <td className={tdClass}>{plan.effective_start_date} → {plan.effective_end_date || 'Open'}</td>
                          <td className={tdClass}><StatusPill status={state} /></td>
                          <td className={tdClass}>
                            <button type="button" className={btnSecondary} onClick={() => loadPlanDetail(plan.spiff_plan_id)}>
                              {state === 'draft' ? 'Review / Submit' : state === 'pending_approval' ? 'Review / Approve' : 'View'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* TAB: Position Assignments                                           */}
      {/* ------------------------------------------------------------------ */}
      {activeTab === 'assignments' && (
        <div className="flex flex-col gap-6">
          <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
            <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Assign SPIFF plan to position</h3>
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
              Only approved plans may be assigned. Multiple different SPIFF plans may be active
              for the same position/period. Fiscal period must overlap plan effective dates.
            </p>
            {assignFormError && (
              <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">{assignFormError}</div>
            )}
            {approvedPlans.length === 0 && (
              <div className="rounded-lg bg-[rgb(var(--color-warning))]/10 border border-[rgb(var(--color-warning))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-warning))]">
                No approved SPIFF plans available. Approve a plan before assigning.
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <label className={labelClass}>SPIFF plan
                <select className={inputClass} value={assigningPlanId}
                  onChange={(e) => setAssigningPlanId(e.target.value)}
                  disabled={approvedPlans.length === 0}>
                  <option value="">Select approved plan</option>
                  {approvedPlans.map((p) => (
                    <option key={p.spiff_plan_id} value={p.spiff_plan_id}>
                      {p.plan_name} ({p.plan_code})
                    </option>
                  ))}
                </select>
              </label>
              <label className={labelClass}>Position
                <select className={inputClass} value={assignForm.position_id}
                  onChange={(e) => updateAssignForm('position_id', e.target.value)}>
                  <option value="">Select position</option>
                  {positions.map((p) => (
                    <option key={p.position_id} value={p.position_id}>
                      {p.full_name || `Position ${p.position_id}`}
                    </option>
                  ))}
                </select>
              </label>
              <label className={labelClass}>Fiscal period ID
                <input type="number" className={inputClass} value={assignForm.fiscal_period_id}
                  onChange={(e) => updateAssignForm('fiscal_period_id', e.target.value)}
                  placeholder={period?.fiscal_period_id ?? 'e.g. 11558'} />
              </label>
            </div>
            <div className="flex items-center gap-2">
              <button type="button" className={btnPrimary} onClick={createAssignment}
                disabled={assignSaving || approvedPlans.length === 0}>
                {assignSaving ? 'Assigning…' : 'Create assignment'}
              </button>
            </div>
          </div>

          <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Current assignments</h3>
              <button type="button" className={btnSecondary} onClick={loadAssignments} disabled={assignmentsLoading}>Refresh</button>
            </div>
            {assignmentsLoading && <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading…</p>}
            {assignmentsError && (
              <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">{assignmentsError}</div>
            )}
            {!assignmentsLoading && assignments.length === 0 && (
              <p className="text-tok-sm text-[rgb(var(--color-text-muted))] italic">No SPIFF assignments for this period.</p>
            )}
            {assignments.length > 0 && (
              <div className="overflow-x-auto">
                <table className="min-w-full text-tok-sm border-collapse">
                  <thead>
                    <tr>
                      <th className={thClass}>SPIFF plan</th>
                      <th className={thClass}>Position</th>
                      <th className={thClass}>Fiscal period</th>
                      <th className={thClass}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assignments.map((a) => (
                      <tr key={a.spiff_position_assignment_id} className="hover:bg-[rgb(var(--color-surface-muted))]">
                        <td className={tdClass}>{planNameById[String(a.spiff_plan_id)] || a.spiff_plan_id}</td>
                        <td className={tdClass}>{positionNameById[String(a.position_id)] || a.position_id}</td>
                        <td className={tdClass}>{a.fiscal_period_id}</td>
                        <td className={tdClass}>{a.assignment_status || 'active'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* TAB: Manager Awards (manager_assigned trigger_type only)            */}
      {/* ------------------------------------------------------------------ */}
      {activeTab === 'awards' && (
        <div className="flex flex-col gap-6">
          <div className="flex justify-end">
            <button type="button" className={btnPrimary}
              onClick={() => { setShowAwardForm(true); setAwardFormError(''); }} disabled={showAwardForm}>
              + New award
            </button>
          </div>

          {awardsError && (
            <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">{awardsError}</div>
          )}

          {managerAssignedApprovedPlans.length === 0 && (
            <div className="rounded-lg bg-[rgb(var(--color-warning))]/10 border border-[rgb(var(--color-warning))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-warning))]">
              No approved manager-assigned SPIFF plans. Awards require an approved
              plan with <code>trigger_type = manager_assigned</code>.
            </div>
          )}

          {showAwardForm && (
            <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
              <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">New manager award</h3>
              {awardFormError && (
                <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">{awardFormError}</div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className={labelClass}>SPIFF plan (manager-assigned)
                  <select className={inputClass} value={awardForm.spiff_plan_id}
                    onChange={(e) => updateAwardForm('spiff_plan_id', e.target.value)}>
                    <option value="">Select plan</option>
                    {managerAssignedApprovedPlans.map((p) => (
                      <option key={p.spiff_plan_id} value={p.spiff_plan_id}>
                        {p.plan_name} ({p.plan_code})
                      </option>
                    ))}
                  </select>
                </label>
                <label className={labelClass}>Position
                  <select className={inputClass} value={awardForm.position_id}
                    onChange={(e) => updateAwardForm('position_id', e.target.value)}>
                    <option value="">Select position</option>
                    {positions.map((p) => (
                      <option key={p.position_id} value={p.position_id}>
                        {p.full_name || `Position ${p.position_id}`}
                      </option>
                    ))}
                  </select>
                </label>
                <label className={labelClass}>Fiscal period ID
                  <input type="number" className={inputClass} value={awardForm.fiscal_period_id}
                    onChange={(e) => updateAwardForm('fiscal_period_id', e.target.value)}
                    placeholder={period?.fiscal_period_id ?? 'e.g. 11558'} />
                </label>
                <label className={labelClass}>Award amount
                  <input type="number" min="0" step="0.01" className={inputClass}
                    value={awardForm.award_amount}
                    onChange={(e) => updateAwardForm('award_amount', e.target.value)}
                    placeholder="0.00" />
                </label>
                <label className={[labelClass, 'sm:col-span-2'].join(' ')}>Award reason
                  <textarea rows={2} className={inputClass} value={awardForm.award_reason}
                    onChange={(e) => updateAwardForm('award_reason', e.target.value)}
                    placeholder="e.g. Strategic executive-sponsored deal" />
                </label>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" className={btnPrimary} onClick={createAward}
                  disabled={awardSaving || managerAssignedApprovedPlans.length === 0}>
                  {awardSaving ? 'Creating…' : 'Create award'}
                </button>
                <button type="button" className={btnSecondary}
                  onClick={() => { setShowAwardForm(false); setAwardForm(EMPTY_AWARD_FORM); setAwardFormError(''); }}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Awards table */}
          <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Manager awards</h3>
              <button type="button" className={btnSecondary} onClick={loadAwards} disabled={awardsLoading}>Refresh</button>
            </div>
            {awardsLoading && <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading…</p>}
            {!awardsLoading && awards.length === 0 && (
              <p className="text-tok-sm text-[rgb(var(--color-text-muted))] italic">No SPIFF awards for this period.</p>
            )}
            {awards.length > 0 && (
              <div className="overflow-x-auto">
                <table className="min-w-full text-tok-sm border-collapse">
                  <thead>
                    <tr>
                      <th className={thClass}>SPIFF plan</th>
                      <th className={thClass}>Position</th>
                      <th className={thClass}>Amount</th>
                      <th className={thClass}>Reason</th>
                      <th className={thClass}>Status</th>
                      <th className={thClass} aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {awards.map((award) => {
                      const awardState = deriveState(award);
                      return (
                        <tr key={award.spiff_award_id} className="hover:bg-[rgb(var(--color-surface-muted))]">
                          <td className={tdClass}>{planNameById[String(award.spiff_plan_id)] || award.spiff_plan_id}</td>
                          <td className={tdClass}>{positionNameById[String(award.position_id)] || award.position_id}</td>
                          <td className={tdClass}>
                            {award.award_amount != null
                              ? Number(award.award_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })
                              : '—'}
                          </td>
                          <td className={tdClass}>{award.award_reason || '—'}</td>
                          <td className={tdClass}><StatusPill status={awardState} /></td>
                          <td className={tdClass}>
                            <button type="button" className={btnSecondary}
                              onClick={() => setSelectedAward(award)}>
                              {awardState === 'draft' ? 'Review / Submit' : awardState === 'pending_approval' ? 'Review / Approve' : 'View'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
